// @owners { team: patients-team }
import {
  Body,
  Button,
  Card,
  InputPassword,
  InputText,
  LgPadding,
  LoadingEmptyState,
  SecondaryPage,
  Toast,
  ToastContext,
  XlPadding,
  XlSpacing,
} from '@alto/design-system';
import { useQueryCreateAccountConfigs, useQuerySuccessConfigs } from '@alto/features';
import { useNavigation } from '@alto/navigation';
// eslint-disable-next-line @alto/prefer-react-hook-form
import { Form, Formik } from 'formik';
import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// eslint-disable-next-line import/no-deprecated
import { updateOnboardingUser } from '~shared/actions/onboarding';
import { getCompanyName } from '~shared/config/getCompanyName';
import { PASSWORD_ERROR_MESSAGE, getVerifyFailureErrorMessage } from '~shared/constants';
import {
  CREATE_ACCOUNT,
  FERTILITY_SUPPORT,
  ONBOARD_STATE_DONE,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import getError from '~shared/features/ui/selectors/getErrors';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { getIsCurrentUserFertility } from '~shared/features/users/selectors/getIsFertilityUser';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { getErrorMessage } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { useFeatureDisablingMapping } from '~shared/hooks/useIsFeatureDisabledForCompany';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useGetInsurances } from '~shared/queries/query-keys/insurances';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { createValidator, validateEmail, validatePassword, validatePresentWithMessage } from '~shared/validations';
import { useNavigationContext } from '~web/contexts';
import { OnboardingHeader } from '~web/features/onboarding/components/redesign/OnboardingHeader';
import { OnboardingNavBarLogo } from '~web/features/onboarding/components/redesign/OnboardingNavBarLogo';
import { previousUrl as getOnboardingPreviousUrl, nextUrl } from '~web/features/onboarding/helpers/urlHandler';

const initialValues = {
  email: '',
  password: '',
};

export type Values = {
  email: string;
  password: string;
};

export const OnboardingCreateAccountPage = () => {
  const { navigate } = useNavigation();
  const location = useLocation();
  const { addToast } = useContext(ToastContext);
  const { updateOnboardingUserLoading } = useSelectorShared(getLoading);
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const showFertility = useSelectorShared(getIsCurrentUserFertility);
  const { trackPageView } = useAnalytics();
  const dispatch = useDispatchShared();
  const { previous: previousWebUrl } = useNavigationContext();
  const origin = previousWebUrl ? routeFromPath(previousWebUrl) : '';
  const currentRoute = routeFromPath(location.pathname);
  const { disabledFeatures, isLoading } = useFeatureDisablingMapping();
  const shouldDisableUnboxingGuide = disabledFeatures.unboxing_guide;
  const shouldDisableInstructionalVideos = disabledFeatures.app_instructional_videos;

  const shouldSkipFertilitySupport = shouldDisableUnboxingGuide || shouldDisableInstructionalVideos;
  const showFertilitySupport = (showProgyny || showFertility) && !shouldSkipFertilitySupport;
  const nextRoute = nextUrl({
    currentRoute: CREATE_ACCOUNT,
    desiredRoute: showFertilitySupport ? FERTILITY_SUPPORT : undefined,
  });
  const { hasInsurance } = useGetInsurances();
  const onboardingPreviousUrl = getOnboardingPreviousUrl({ params: { hasInsurance }, currentRoute });
  const { updateOnboardingUserError } = useSelectorShared(getError);
  const { headerBackgroundColor, headerLogoUrl, isFetching, partnershipName } = useQueryCreateAccountConfigs();

  // Prefetch onboarding success config before account creation.
  useQuerySuccessConfigs();

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING__CREATE_LOGIN_VIEWED,
      params: { origin, is_progyny_rx_view: showProgyny },
    });
  }, [trackPageView, showProgyny, origin]);

  const validate = createValidator({
    email: (email) => validatePresentWithMessage('Email')(email) || validateEmail(email),
    password: (password) => validatePresentWithMessage('Password')(password) || validatePassword(password),
  });

  const handleBack = () => {
    if (onboardingPreviousUrl) {
      navigate(onboardingPreviousUrl);
    }
  };

  const handleSave = async (data: Values) => {
    // IMPORTANT! This is the last step tracked step of onboarding.
    // Don't call `updateOnboardingUser` after this page because the patient is
    // re-issued a token and navigates to the homescreen after any additional information pages
    // (i.e. FertilitySupport for progyny and fertility patients)
    // eslint-disable-next-line import/no-deprecated
    const success = await dispatch(updateOnboardingUser({ ...data, onboard_state: ONBOARD_STATE_DONE }));
    if (success) {
      navigate(nextRoute);
    } else {
      addToast(
        <Toast variant="error">{getErrorMessage(updateOnboardingUserError) || getVerifyFailureErrorMessage()}</Toast>,
      );
    }
  };

  const titleText = showProgyny ? `Create your ${getCompanyName()} login` : 'Create your account login';

  if (isFetching || isLoading) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSave}
      validate={validate}
      enableReinitialize
    >
      {(formikProps) => (
        <Form
          {...formikProps}
          noValidate
        >
          <SecondaryPage
            withoutWebFramingElementHeights
            buttons={[
              <Button
                type="primary"
                key="finish"
                label="Finish"
                onPress={formikProps.handleSubmit}
                loading={(updateOnboardingUserLoading && formikProps.submitCount > 0) ?? false}
              />,
            ]}
            footerPlacementContext="webScreenWithoutPolicyFooter"
            dismissIcon="chevronleft"
            onDismiss={handleBack}
            headerBackgroundColor={headerBackgroundColor}
            HeaderContent={
              <OnboardingHeader
                title={titleText}
                subtitle={
                  <Body center>
                    You'll use this email address and password to log into your {getCompanyName()} account.
                  </Body>
                }
              />
            }
            NavBarCenterContent={
              <OnboardingNavBarLogo
                illustrationSrc={headerLogoUrl}
                partnershipName={partnershipName}
              />
            }
          >
            {updateOnboardingUserLoading ? (
              <LoadingEmptyState />
            ) : (
              <Card>
                <LgPadding>
                  <InputText
                    id="email"
                    accessibilityLabel="Email address"
                    label="Email address"
                    value={formikProps.values.email}
                    placeholder="username@email.com"
                    autoCapitalize="none"
                    error={formikProps.submitCount > 0 ? formikProps.errors.email : undefined}
                    onChangeText={formikProps.handleChange('email')}
                    required
                  />
                  <XlSpacing />
                  <InputPassword
                    accessibilityLabel="password"
                    label="Create password"
                    value={formikProps.values.password}
                    error={formikProps.submitCount > 0 ? formikProps.errors.password : undefined}
                    onChangeText={formikProps.handleChange('password')}
                    placeholder="Enter password"
                    caption={PASSWORD_ERROR_MESSAGE}
                  />
                </LgPadding>
              </Card>
            )}
          </SecondaryPage>
        </Form>
      )}
    </Formik>
  );
};
